import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ArrowRight } from "lucide-react";
import MarqueeCmp from "@/components/Marquee";
import VeteranBadge from "@/components/VeteranBadge";
import InstagramFeed from "@/components/InstagramFeed";
import ReviewsMarquee from "@/components/ReviewsMarquee";
import { useSite } from "@/context/SiteContext";
import { resolveImage } from "@/lib/siteDefaults";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const { site } = useSite();
  const s = site;

  useEffect(() => {
    api.get("/products?featured=true").then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  return (
    <div className="pt-16 md:pt-20">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <img
          src={resolveImage(s.hero.image)}
          alt="Movement in light"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#F9F9F6]/70"></div>

        <div className="relative w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pb-16 md:pb-24 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8 fade-up">
            <VeteranBadge variant="dark" text={s.hero.badge_text} className="mb-6" />
            <p className="text-xs uppercase tracking-[0.35em] text-[#1A1C1A]/70 font-semibold mb-6">
              {s.hero.overline}
            </p>
            <h1 className="font-serif font-light text-[#1A1C1A] text-6xl sm:text-7xl md:text-8xl lg:text-[10rem] leading-[0.9] tracking-tighter">
              {s.hero.title_line_1}
              <br />
              <em className="italic font-light text-stone-600">{s.hero.title_em}</em>
              <br />
              {s.hero.title_line_3}
            </h1>
          </div>
          <div className="md:col-span-4 fade-up fade-up-delay-2 md:pb-3">
            <p className="text-base leading-relaxed text-[#1A1C1A]/80 font-light max-w-sm">
              {s.hero.subtitle}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to={s.hero.cta_primary_href || "/shop"}
                data-testid="hero-shop-cta"
                className="inline-flex items-center gap-2 bg-[#1A1C1A] text-[#F9F9F6] hover:bg-[#3C3E3C] px-7 py-3.5 rounded-full transition-all duration-300 font-sans text-sm tracking-wide"
              >
                {s.hero.cta_primary}
                <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
              </Link>
              <Link
                to={s.hero.cta_secondary_href || "/about"}
                className="inline-flex items-center gap-2 border border-[#1A1C1A]/70 text-[#1A1C1A] hover:bg-[#F4F1EA] px-7 py-3.5 rounded-full transition-all duration-300 font-sans text-sm tracking-wide"
              >
                {s.hero.cta_secondary}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <MarqueeCmp />

      {/* CATEGORIES BENTO */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-32">
        <div className="flex items-end justify-between mb-10 md:mb-14">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-4">{s.categories.overline}</p>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light tracking-tight text-[#1A1C1A]">
              {s.categories.heading_line_1} <em className="italic text-stone-600">{s.categories.heading_em}</em>{s.categories.heading_line_3}
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          <Link to="/shop?category=gym" className="md:col-span-7 group relative aspect-[4/5] md:aspect-[7/6] overflow-hidden rounded-2xl bg-[#F4F1EA]">
            <img src={resolveImage(s.categories.gym.image)} alt={s.categories.gym.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms]" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>
            <div className="absolute bottom-0 left-0 p-8 md:p-12 text-white">
              <p className="text-xs uppercase tracking-[0.3em] mb-3 opacity-90">{s.categories.gym.overline}</p>
              <h3 className="font-serif text-4xl md:text-6xl font-light tracking-tight">{s.categories.gym.title}</h3>
              <p className="mt-3 max-w-md font-light opacity-90">{s.categories.gym.subtitle}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm tracking-wide border-b border-white/60 pb-1">{s.categories.gym.cta_label} <ArrowRight strokeWidth={1.5} className="w-4 h-4" /></span>
            </div>
          </Link>

          <Link to="/shop?category=beauty" className="md:col-span-5 group relative aspect-[4/5] overflow-hidden rounded-2xl bg-[#EBE8E0]">
            <img src={resolveImage(s.categories.beauty.image)} alt={s.categories.beauty.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms]" />
            <div className="absolute inset-0 bg-gradient-to-t from-white/60 via-white/0 to-transparent"></div>
            <div className="absolute bottom-0 left-0 p-8 md:p-10 text-[#1A1C1A]">
              <p className="text-xs uppercase tracking-[0.3em] mb-3 opacity-70">{s.categories.beauty.overline}</p>
              <h3 className="font-serif text-4xl md:text-5xl font-light tracking-tight">{s.categories.beauty.title}</h3>
              <p className="mt-3 max-w-sm font-light opacity-80">{s.categories.beauty.subtitle}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm tracking-wide border-b border-[#1A1C1A]/60 pb-1">{s.categories.beauty.cta_label} <ArrowRight strokeWidth={1.5} className="w-4 h-4" /></span>
            </div>
          </Link>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {featured.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pb-24 md:pb-32">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-4">{s.featured.overline}</p>
              <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight">{s.featured.heading_line_1} <em className="italic text-stone-600">{s.featured.heading_em}</em>{s.featured.heading_line_3}</h2>
            </div>
            <Link to="/shop" className="hidden md:inline-flex items-center gap-2 text-sm tracking-wide text-[#1A1C1A] hover:text-stone-500 transition-colors border-b border-[#1A1C1A]/40 pb-1">
              View all <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-10">
            {featured.slice(0, 4).map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* BRAND STORY */}
      <section className="bg-[#F4F1EA]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-40 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <p className="text-xs uppercase tracking-[0.3em] text-stone-500 font-semibold mb-6">{s.brand_story.overline}</p>
            <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter text-[#1A1C1A] leading-[0.95]">
              {s.brand_story.heading_line_1}<br />
              <em className="italic text-stone-600">{s.brand_story.heading_em}</em>{s.brand_story.heading_line_3}
            </h2>
            <p className="mt-8 text-lg font-light text-stone-700 leading-relaxed max-w-lg">
              {s.brand_story.body}
            </p>
            <Link to="/about" className="mt-10 inline-flex items-center gap-2 text-[#1A1C1A] border-b border-[#1A1C1A]/60 pb-1 text-sm tracking-wide hover:text-stone-500 transition-colors">
              {s.brand_story.cta_label} <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
            </Link>
          </div>
          <div className="md:col-span-6 order-1 md:order-2 grid grid-cols-5 gap-4">
            <img src={resolveImage(s.brand_story.image_1)} alt="Lookbook" className="col-span-3 aspect-[3/4] object-cover rounded-2xl" />
            <img src={resolveImage(s.brand_story.image_2)} alt="Sunlight" className="col-span-2 aspect-[3/5] object-cover rounded-2xl mt-12" />
            <img src={resolveImage(s.brand_story.image_3)} alt="Architecture" className="col-span-5 aspect-[16/9] object-cover rounded-2xl" />
          </div>
        </div>
      </section>

      <InstagramFeed />

      <ReviewsMarquee />

      <MarqueeCmp variant="secondary" />
    </div>
  );
}
